import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import { PorscheDesignSystemProvider } from '@porsche-design-system/components-react';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import './App.css';
import { Epicmap } from './components/Epicmap';
import { Featuremap } from './components/Featuremap';
import { NavigationBar } from './components/NavigationBar';
import { Spinner } from './components/Spinner';
import StatusColorLegend from './components/StatusColorLegend';
import { useJiraData } from './hooks/useJiraData';
import { isValidVersion } from './utils/versionUtils';

// Default JQL query for "All unresolved Features"
const DEFAULT_JQL_QUERY = "project = ARTDIAGUPD AND type = Feature AND resolution = Unresolved ORDER BY status ASC";

//Compares two version strings based on year and quarter.
const sortVersions = (a, b) => {
  const matchA = a.match(/^PI-(\d{2})\.(\d+)$/);
  const matchB = b.match(/^PI-(\d{2})\.(\d+)$/);
  const yearA = matchA ? parseInt(matchA[1], 10) : 0;
  const quarterA = matchA ? parseInt(matchA[2], 10) : 0;
  const yearB = matchB ? parseInt(matchB[1], 10) : 0;
  const quarterB = matchB ? parseInt(matchB[2], 10) : 0;
  return yearA === yearB ? quarterA - quarterB : yearA - yearB;
};

// Extracts, filters and sorts valid versions from a list of issues.
const extractValidSortedVersions = (issues) => {
  const versions = Array.from(
    new Set(issues.flatMap((issue) => issue.fields.versions?.map((version) => version.name) || []))
  ).filter(isValidVersion);

  return versions.sort(sortVersions);
};

/**
 * The main component of the application.
 * Handles authentication, data loading, routing and UI state like expand/collapse.
 *
 * @component
 * @returns {JSX.Element}
 */
const App = () => {
  const [expandState, setExpandState] = useState('collapsed');
  const { instance, accounts, inProgress } = useMsal();
  const { postJQLQuery, issues, isLoading } = useJiraData();
  const hasExecutedInitialQuery = useRef(false);

  // Extract and sort valid versions only once when issues change
  const sortedVersions = useMemo(() => {
    return issues?.length > 0 ? extractValidSortedVersions(issues) : [];
  }, [issues]);

  useEffect(() => {
    if (accounts.length > 0 && inProgress === 'none') {
      // Set active account if not already set
      const activeAccount = instance.getActiveAccount();
      if (!activeAccount) {
        instance.setActiveAccount(accounts[0]);
      }
      
      // Automatically execute the default query after authentication is complete, but only once
      if (!hasExecutedInitialQuery.current) {
        // Add a small delay to ensure authentication is fully complete
        setTimeout(() => {
          postJQLQuery(DEFAULT_JQL_QUERY);
          hasExecutedInitialQuery.current = true;
        }, 100);
      }
    } else if (accounts.length === 0 && inProgress === 'none') {
      instance.loginRedirect().catch((e) => {
        console.error(e);
      });
    }
  }, [accounts, inProgress, instance, postJQLQuery]);

  const refreshData = useCallback(() => {
    setExpandState('none');
    postJQLQuery(DEFAULT_JQL_QUERY);
  }, [postJQLQuery]);

  return (
    <div className="relative min-h-screen background-gradient">
      <UnauthenticatedTemplate>
        <h5 className="card-title">Please sign-in to see your profile information.</h5>
      </UnauthenticatedTemplate>

      <AuthenticatedTemplate>
        <Router>
          <PorscheDesignSystemProvider>
            <NavigationBar
              onExpandAll={() => setExpandState('expanded')}
              onCollapseAll={() => setExpandState('collapsed')}
              onRefresh={refreshData}
            />

            <main className="flex-1 overflow-auto px-8 content-area custom-scroll">
              <Routes>
                <Route
                  path="/"
                  element={
                    <Featuremap
                      issues={issues}
                      versions={sortedVersions}
                      expandState={expandState}
                    />
                  }
                />
                <Route
                  path="/epicmap"
                  element={
                    <Epicmap issues={issues} versions={sortedVersions} expandState={expandState} />
                  }
                />
              </Routes>

              {isLoading && <Spinner />}

              <StatusColorLegend />
            </main>
          </PorscheDesignSystemProvider>
        </Router>
      </AuthenticatedTemplate>
    </div>
  );
};

export default App;
